import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { Badge, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Input } from "reactstrap";
import { Link, useHistory } from "react-router-dom";

const NotificationsPage = () => {
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    loadNotifications();
  }, [currentPage, filter, searchText]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemPerPage,
        status: filter !== "all" ? filter : undefined,
      };
      const response = await api.getNotifications(params);
      let notificationsList = response.data || [];

      if (searchText) {
        const searchLower = searchText.toLowerCase();
        notificationsList = notificationsList.filter(
          (n) =>
            (n.title && n.title.toLowerCase().includes(searchLower)) ||
            (n.message && n.message.toLowerCase().includes(searchLower))
        );
      }

      setNotifications(notificationsList);
      setTotalItems(response.total || notificationsList.length);
    } catch (error) {
      toast.error("Erreur lors du chargement des notifications");
      setNotifications([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.markNotificationAsRead(id);
      toast.success("Notification marquée comme lue");
      loadNotifications();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.markAllNotificationsAsRead();
      toast.success("Toutes les notifications ont été marquées comme lues");
      loadNotifications();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette notification ?")) return;
    try {
      await api.deleteNotification(id);
      toast.success("Notification supprimée");
      loadNotifications();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "A l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const resolveDeepLink = (notification) =>
    notification.deep_link ||
    notification.data?.deep_link ||
    (notification.data?.observation_id
      ? `/observations/${notification.data.observation_id}/view`
      : null);

  const openNotificationTarget = (notification, ev) => {
    if (ev?.target?.closest?.(".nk-tb-actions")) return;
    const path = resolveDeepLink(notification);
    if (path) {
      history.push(`${process.env.PUBLIC_URL || ""}${path}`);
    }
  };

  return (
    <React.Fragment>
      <Head title="Notifications" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Notifications</BlockTitle>
              <BlockDes className="text-soft">
                <p>Vos notifications et alertes HSE ({totalItems})</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <div className="form-control-wrap">
                    <div className="form-icon form-icon-left">
                      <Icon name="search"></Icon>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher..."
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ minWidth: "250px" }}
                    />
                  </div>
                </li>
                <li>
                  <UncontrolledDropdown>
                    <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                      <Icon name="filter-alt" className="d-none d-sm-inline"></Icon>
                      <span>{filter === "all" ? "Toutes" : filter === "unread" ? "Non lues" : "Lues"}</span>
                      <Icon name="chevron-right" className="dd-indc"></Icon>
                    </DropdownToggle>
                    <DropdownMenu end>
                      <ul className="link-list-opt no-bdr">
                        <li>
                          <DropdownItem
                            tag="a"
                            href="#all"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setFilter("all");
                              setCurrentPage(1);
                            }}
                          >
                            <span>Toutes</span>
                          </DropdownItem>
                        </li>
                        <li>
                          <DropdownItem
                            tag="a"
                            href="#unread"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setFilter("unread");
                              setCurrentPage(1);
                            }}
                          >
                            <span>Non lues</span>
                          </DropdownItem>
                        </li>
                        <li>
                          <DropdownItem
                            tag="a"
                            href="#read"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setFilter("read");
                              setCurrentPage(1);
                            }}
                          >
                            <span>Lues</span>
                          </DropdownItem>
                        </li>
                      </ul>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </li>
                {notifications.filter((n) => !n.read_at).length > 0 && (
                  <li>
                    <Button color="primary" onClick={handleMarkAllAsRead}>
                      <Icon name="check"></Icon>
                      <span>Tout marquer comme lu</span>
                    </Button>
                  </li>
                )}
                <li>
                  <Button tag={Link} to="/alerts" color="light" outline>
                    <Icon name="alert-circle"></Icon>
                    <span>Centre d&apos;alertes</span>
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <DataTable className="card-stretch">
            <DataTableBody>
              <DataTableHead className="nk-tb-item nk-tb-head">
                <DataTableRow>
                  <span className="sub-text">Notification</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Date</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Statut</span>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-tools"></DataTableRow>
              </DataTableHead>

            {loading ? (
              <DataTableItem>
                <DataTableRow>
                  <DataTableItem colSpan={4} className="text-center">
                    <Spinner color="primary" />
                  </DataTableItem>
                </DataTableRow>
              </DataTableItem>
            ) : notifications.length === 0 ? (
              <DataTableItem>
                <DataTableRow>
                  <DataTableItem colSpan={4} className="text-center">
                    <div className="text-center py-4">
                      <span className="text-soft">
                        {searchText
                          ? "Aucune notification ne correspond a votre recherche"
                          : filter === "unread"
                          ? "Aucune notification non lue"
                          : filter === "read"
                          ? "Aucune notification lue"
                          : "Aucune notification"}
                      </span>
                    </div>
                  </DataTableItem>
                </DataTableRow>
              </DataTableItem>
            ) : (
              notifications.map((notification) => (
                <DataTableItem
                  key={notification.id}
                  className={!notification.read_at ? "nk-tb-item-unread" : ""}
                  onClick={(e) => openNotificationTarget(notification, e)}
                  style={resolveDeepLink(notification) ? { cursor: "pointer" } : undefined}
                >
                  <DataTableRow>
                    <div className="d-flex align-items-center">
                      <div className="nk-notification-icon me-3">
                        <Icon name={notification.icon || "bell"} className={`icon-circle ${notification.iconStyle || ""}`} />
                      </div>
                      <div>
                        <span className="tb-lead">{notification.title || "Notification"}</span>
                        <br />
                        <span className="tb-sub text-soft">{notification.message || ""}</span>
                      </div>
                    </div>
                  </DataTableRow>
                  <DataTableRow>
                    <span className="tb-sub">{formatTime(notification.created_at)}</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    {notification.read_at ? <Badge color="success">Lue</Badge> : <Badge color="warning">Non lue</Badge>}
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools">
                    <ul className="nk-tb-actions gx-1">
                      {!notification.read_at && (
                        <li>
                          <button
                            className="btn btn-trigger btn-icon"
                            onClick={() => handleMarkAsRead(notification.id)}
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Marquer comme lu"
                          >
                            <Icon name="check"></Icon>
                          </button>
                        </li>
                      )}
                      <li className="nk-tb-action-hidden">
                        <button
                          className="btn btn-trigger btn-icon text-danger"
                          onClick={() => handleDelete(notification.id)}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Supprimer"
                        >
                          <Icon name="trash"></Icon>
                        </button>
                      </li>
                    </ul>
                  </DataTableRow>
                </DataTableItem>
              ))
            )}
            </DataTableBody>
            <div className="card-inner">
              {totalItems > 0 && (
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={totalItems}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              )}
            </div>
          </DataTable>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default NotificationsPage;

